import { useCallback, useState } from "react";

import {
  createSearchSubscription,
  deleteSearchSubscription,
} from "@/api/endpoints/alert-subscriptions";
import { getOrganizationUserSearchSubscriptions } from "@/api/endpoints/organization";
import { SearchSubscriptionInterval } from "@/constants";
import { useOrganization } from "@/hooks/useOrganization";
import { SearchSubscription } from "@/types/SearchSubscription";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

/**
 * Barebones hook to fetch the subscriptions for the current user and organization
 */
const useSubscriptions = (userId?: number) => {
  const { data: orgData } = useOrganization();
  const { uuid: organization_uuid = "" } = orgData?.ok ? orgData.data : {};

  return useQuery({
    queryKey: [`subscriptions-${userId || "me"}-${organization_uuid}`],
    queryFn: () =>
      getOrganizationUserSearchSubscriptions({
        user_id: userId || "me",
        organization_uuid,
      }),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });
};

/**
 * Hook to handle the subscription state for a given search, user and organization.
 * - Made generic as to be able to reuse the logic across different components and places.
 */
export const useSubscriptionHandler = ({
  search_uuid,
  user_id,
  organization_uuid,
}: {
  search_uuid: string;
  user_id: number;
  organization_uuid: string;
}) => {
  const [isSaving, setSaving] = useState(false);

  // This hook returns data for all searches...
  const { data, isLoading, refetch } = useSubscriptions(user_id);
  const { subscriptions: allSubscriptions = [] } = data?.ok ? data.data : {};
  //... so we need to filter out the ones we need

  const subscriptions =
    allSubscriptions
      .filter((s) => s.search_uuid === search_uuid)
      // This second filter is only for purposes of being compatible with the still existing none-interval
      .filter((s) => s.interval !== ("none" as unknown)) || [];

  const subscriptionsByInterval = subscriptions.reduce<
    Partial<Record<SearchSubscriptionInterval, SearchSubscription>>
  >((acc, sub) => {
    acc[sub.interval] = sub;
    return acc;
  }, {});

  // Wrapper around a promise that sets the saving state and revalidates the state on completion
  const revalidate = useCallback(
    async (promise: Promise<unknown>) => {
      setSaving(true);
      return promise.finally(() => {
        setSaving(false);
        // Something changed. Force a revalidation
        refetch();
      });
    },
    [refetch]
  );

  // Unsubscribe all subscriptions at once. This fires a whole array of delete requests
  // an waits for all of them to finish before revalidating
  const unsubscribeAll = () => {
    if (!subscriptions) return;
    revalidate(
      Promise.all(
        subscriptions.map((sub) =>
          deleteSearchSubscription({
            user_id,
            organization_uuid,
            sub_id: sub.id,
          })
        )
      )
    );
  };

  // Create a new subscription for a given interval
  const subscribe = useCallback(
    (interval: SearchSubscriptionInterval) => {
      revalidate(
        createSearchSubscription({
          user_id,
          organization_uuid,
          interval,
          search_uuid,
        })
      );
    },
    [revalidate, organization_uuid, user_id, search_uuid]
  );

  // Delets a given subscription
  const unsubscribe = (subscription: SearchSubscription) => {
    revalidate(
      deleteSearchSubscription({
        user_id,
        organization_uuid,
        sub_id: subscription.id,
      })
    );
  };

  return {
    subscribe,
    unsubscribe,
    unsubscribeAll,
    subscriptions,
    subscriptionsByInterval,
    isSaving,
    isLoading,
  };
};
